
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { Bell, Mail, Settings as SettingsIcon } from "lucide-react";
import { NotificationType } from "@/types/notifications";

interface NotificationSettingsProps {
  settings: any;
  onSettingsChange: (settings: any) => void;
}

const NotificationSettings = ({ settings, onSettingsChange }: NotificationSettingsProps) => {
  const { preferences, updatePreference, isLoading } = useNotificationPreferences();

  const notificationTypes: Array<{ type: NotificationType; label: string; description: string }> = [
    { type: 'product_created', label: 'New Products', description: 'When new products are added' },
    { type: 'product_updated', label: 'Product Updates', description: 'When products are modified' },
    { type: 'product_low_stock', label: 'Low Stock Alerts', description: 'When products are running low' },
    { type: 'product_out_of_stock', label: 'Out of Stock', description: 'When products are out of stock' },
    { type: 'order_created', label: 'New Orders', description: 'When new orders are placed' },
    { type: 'order_updated', label: 'Order Updates', description: 'When order status changes' },
    { type: 'order_high_value', label: 'High Value Orders', description: 'For orders above threshold' },
    { type: 'order_payment_failed', label: 'Payment Failures', description: 'When payments fail' },
    { type: 'user_registered', label: 'New Users', description: 'When users register' },
    { type: 'system_error', label: 'System Errors', description: 'When system errors occur' },
    { type: 'security_alert', label: 'Security Alerts', description: 'Security-related notifications' },
  ];

  const handlePreferenceUpdate = async (type: NotificationType, field: 'push_enabled' | 'email_enabled', value: boolean) => {
    updatePreference({
      notificationType: type,
      updates: { [field]: value }
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-muted-foreground">Loading notification preferences...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* General Notification Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <SettingsIcon className="h-5 w-5" />
            General Settings
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Configure overall notification behavior
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-foreground">Enable All Notifications</Label>
              <p className="text-xs text-muted-foreground">
                Master toggle for all notification types
              </p>
            </div>
            <Switch
              checked={settings.emailNotifications !== false}
              onCheckedChange={(checked) => onSettingsChange({ emailNotifications: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-foreground">Email Notifications</Label>
              <p className="text-xs text-muted-foreground">
                Send notifications via email
              </p>
            </div>
            <Switch
              checked={settings.systemUpdates !== false}
              onCheckedChange={(checked) => onSettingsChange({ systemUpdates: checked })}
            />
          </div>
        </CardContent>
      </Card>

      {/* In-App Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <Bell className="h-5 w-5" />
            In-App Notifications
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Configure which notifications appear in the admin panel
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {notificationTypes.map((notif) => {
            const pref = preferences.find(p => p.notification_type === notif.type);
            return (
              <div key={notif.type} className="flex items-center justify-between py-2">
                <div className="space-y-0.5">
                  <Label className="text-foreground">{notif.label}</Label>
                  <p className="text-xs text-muted-foreground">
                    {notif.description}
                  </p>
                </div>
                <Switch
                  checked={pref?.push_enabled !== false}
                  onCheckedChange={(checked) => handlePreferenceUpdate(notif.type, 'push_enabled', checked)}
                />
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Email Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Notifications
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Configure which notifications are sent via email
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {notificationTypes.map((notif) => {
            const pref = preferences.find(p => p.notification_type === notif.type);
            return (
              <div key={notif.type} className="flex items-center justify-between py-2">
                <div className="space-y-0.5">
                  <Label className="text-foreground">{notif.label}</Label>
                  <p className="text-xs text-muted-foreground">
                    Email alerts for {notif.description.toLowerCase()}
                  </p>
                </div>
                <Switch
                  checked={pref?.email_enabled === true}
                  onCheckedChange={(checked) => handlePreferenceUpdate(notif.type, 'email_enabled', checked)}
                  disabled={pref?.push_enabled === false}
                />
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationSettings;
